import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import { fetchStaffById, updateStaff } from '@/api/staff';
import DuitNowQrUpload from './DuitNowQrUpload';

const inputClass =
  'block w-full rounded-[0.5rem] border border-slate-200/80 dark:border-darkmode-400 bg-white dark:bg-darkmode-600 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 shadow-sm focus:border-primary focus:ring-4 focus:ring-primary/20';

function Main() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [memberCode, setMemberCode] = useState<string | null>(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [hasDuitNowQr, setHasDuitNowQr] = useState(false);

  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLoading(true);
    setLoadError('');

    void fetchStaffById(id)
      .then((staff) => {
        if (cancelled) return;
        setName(staff.name);
        setMobile(staff.mobile ?? '');
        setEmail(staff.email ?? '');
        setIsActive(staff.isActive);
        setMemberCode(staff.memberCode);
        setIsRegistered(staff.isRegistered);
        setHasDuitNowQr(staff.hasDuitNowQr);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load ambassador');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setFormError('');
    setFormSuccess('');

    const trimmedName = name.trim();
    if (!trimmedName) {
      setFormError('Staff name is required');
      return;
    }

    setSubmitting(true);
    try {
      await updateStaff(id, {
        name: trimmedName,
        mobile: mobile.trim() || undefined,
        email: email.trim() || undefined,
        isActive,
      });
      setFormSuccess('Ambassador updated successfully');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to update ambassador');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[40vh] text-slate-400">
        <Lucide icon="Loader" className="w-6 h-6 animate-spin mr-2" />
        Loading ambassador…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6">
        <div className="box rounded-xl border border-danger/20 px-6 py-5 text-danger">
          {loadError}
        </div>
        <Button
          type="button"
          variant="outline-primary"
          className="mt-4"
          onClick={() => navigate('/ambassador')}
        >
          Back to Ambassadors
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <Link
          to="/ambassador"
          className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-2"
        >
          <Lucide icon="ArrowLeft" className="w-4 h-4 mr-1.5" />
          Back to Ambassadors
        </Link>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
          Edit Ambassador
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Update ambassador details
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 xl:items-start">
        <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
          {memberCode && (
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
              Membership Code:{' '}
              <span className="font-medium text-slate-700 dark:text-slate-200">{memberCode}</span>
            </p>
          )}

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Staff name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              required
            />
            {isRegistered && (
              <p className="mt-1 text-xs text-slate-500">
                This ambassador has registered — changing the name updates their login username.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Mobile
            </label>
            <input
              type="text"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Status
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">
                {isActive ? 'Active' : 'Deactivated'}
              </span>
            </label>
          </div>

          {formError && (
            <div className="px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
              {formError}
            </div>
          )}

          {formSuccess && (
            <div className="px-4 py-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800">
              {formSuccess}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Lucide icon="Save" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Save changes
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              onClick={() => navigate('/ambassador')}
            >
              Cancel
            </Button>
          </div>
        </form>
        </div>

        {id ? <DuitNowQrUpload staffId={id} hasDuitNowQr={hasDuitNowQr} /> : null}
      </div>
    </div>
  );
}

export default Main;
